"use client";

import GenericDatePicker from "./components/GenericDatePicker";
import GenericDateRangePicker from "./components/GenericDateRangePicker";
import { isSameDay } from "@/utils/dateFormatter";
import clsx from "clsx";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

// ==========================
// CONSTANTS
// ==========================
const TRIP_TYPES = {
  ONEWAY: "oneway",
  ROUNDTRIP: "roundtrip",
};

// ==========================
// HELPERS
// ==========================
function computeTodayTomorrow() {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return { today, tomorrow };
}

const TripDatePicker = forwardRef(
  (
    {
      tripType = TRIP_TYPES.ONEWAY,
      initialActiveTab = 0,
      initialDepartureDate,
      initialReturnDate,
      translationNamespace = "common",
      showQuickButtons = false,
      containerClassName = "",
      onChange, // callback: ({ departureDate, returnDate })
      publicHolidays, // optional external holidays
      onOpen,
      onDropdownOpen,
      dayButtonComponent, // optional custom day cell renderer
      getDayMeta, // optional provider for additional day-level data
      inputClassName = "",
      quickButtonsWrapperClassName = "",
      useTomorrowAsDefault = false, // Varsayılan tarih seçimi için flag (false: bugün, true: yarın)
      viewportHeightWrapperActive = false,
    },
    ref,
  ) => {
    const tForm = useTranslationDetector(translationNamespace);

    // Local, generic state (no flight-specific reducer)
    const [activeTab, setActiveTab] = useState(
      tripType === TRIP_TYPES.ONEWAY ? 0 : initialActiveTab || 0,
    );
    const { today, tomorrow } = useMemo(() => computeTodayTomorrow(), []);

    // Default: if no initial date provided, use useTomorrowAsDefault flag to decide
    const defaultDate = useTomorrowAsDefault ? tomorrow : today;
    const [departureDate, setDepartureDate] = useState(
      initialDepartureDate || defaultDate,
    );
    const [returnDate, setReturnDate] = useState(
      initialReturnDate || (tripType === TRIP_TYPES.ONEWAY ? (initialDepartureDate || defaultDate) : null),
    );

    const departureDatePickerRef = useRef(null);
    const returnDatePickerRef = useRef(null);
    const [isDepartureDatePickerOpen, setIsDepartureDatePickerOpen] =
      useState(false);
    const [isReturnDatePickerOpen, setIsReturnDatePickerOpen] = useState(false);

    // Sync external prop changes with internal state
    useEffect(() => {
      if (initialDepartureDate && initialDepartureDate.getTime() !== departureDate?.getTime()) {
        setDepartureDate(initialDepartureDate);
        if (tripType === TRIP_TYPES.ONEWAY) {
          setReturnDate(initialDepartureDate);
        }
      }
    }, [initialDepartureDate?.getTime(), tripType]);

    useEffect(() => {
      if (initialReturnDate && initialReturnDate.getTime() !== returnDate?.getTime()) {
        setReturnDate(initialReturnDate);
      }
    }, [initialReturnDate?.getTime()]);

    

    const isTodaySelected = useMemo(
      () => departureDate && isSameDay(departureDate, today),
      [departureDate, today],
    );
    const isTomorrowSelected = useMemo(
      () => departureDate && isSameDay(departureDate, tomorrow),
      [departureDate, tomorrow],
    );

    const emitChange = useCallback(
      (dep, ret) => {
        onChange?.({ departureDate: dep, returnDate: ret || null });
      },
      [onChange],
    );

    const onSelectDepartureDate = useCallback(
      (date) => {
        setDepartureDate(date);
        setReturnDate(date);
        emitChange(date, date);

        if (tripType === TRIP_TYPES.ROUNDTRIP && activeTab === 1) {
          setTimeout(() => {
            returnDatePickerRef.current?.open();
          }, 100);
        }
      },
      [emitChange, activeTab, tripType],
    );

    const onSelectReturnDate = useCallback(
      (date, triggerDate) => {
        if (isSameDay(triggerDate, departureDate)) {
          setReturnDate(triggerDate);
          emitChange(departureDate, triggerDate);
        } else if (isSameDay(triggerDate, returnDate)) {
          setDepartureDate(triggerDate);
          emitChange(triggerDate, returnDate);
        } else if (returnDate && isSameDay(returnDate, departureDate)) {
          setDepartureDate(date.from);
          setReturnDate(date.to);
          emitChange(date.from, date.to);
          returnDatePickerRef.current?.close?.();
        } else if (returnDate) {
          setDepartureDate(triggerDate);
          setReturnDate(null);
          emitChange(triggerDate, null);
        } else {
          setDepartureDate(date.from);
          setReturnDate(date.to);
          emitChange(date.from, date.to);
          returnDatePickerRef.current?.close?.();
        }
      },
      [departureDate, returnDate, emitChange],
    );

    const handleAddReturnClick = useCallback(() => {
      setActiveTab(1);
      setTimeout(() => {
        setIsReturnDatePickerOpen(true);
        returnDatePickerRef.current?.open();
      }, 100);
    }, []);

    const handleMobileDepartureDateClick = useCallback(() => {
      setActiveTab(0);
      setIsReturnDatePickerOpen(false);
      returnDatePickerRef.current?.close?.();
      setTimeout(() => {
        setIsDepartureDatePickerOpen(true);
        departureDatePickerRef.current?.open?.();
      }, 100);
    }, []);

    const handleMobileReturnDateClick = useCallback(() => {
      setActiveTab(1);
      setIsDepartureDatePickerOpen(false);
      departureDatePickerRef.current?.close?.();
      setTimeout(() => {
        setIsReturnDatePickerOpen(true);
        returnDatePickerRef.current?.open?.();
      }, 150);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        setActiveTab: (tab) => {
          if (tripType === TRIP_TYPES.ROUNDTRIP) {
            setActiveTab(tab);
          }
        },
        setDepartureDate: (date) => {
          setDepartureDate(date);
          emitChange(date, returnDate);
        },
        setReturnDate: (date) => {
          setReturnDate(date);
          emitChange(departureDate, date);
        },
        openDeparturePicker: () => {
          if (tripType === TRIP_TYPES.ROUNDTRIP) {
            setActiveTab(0);
          }
          setTimeout(() => {
            setIsDepartureDatePickerOpen(true);
            departureDatePickerRef.current?.open?.();
          }, 100);
        },
        openReturnPicker: () => {
          if (tripType === TRIP_TYPES.ROUNDTRIP) {
            setActiveTab(1);
            setTimeout(() => {
              setIsReturnDatePickerOpen(true);
              returnDatePickerRef.current?.open?.();
            }, 100);
          }
        },
        getValues: () => ({ activeTab, departureDate, returnDate }),
      }),
      [tripType, activeTab, departureDate, returnDate, emitChange],
    );

    const rootClasses = useMemo(
      () =>
        clsx(
          "flex flex-col items-center rounded-lg bg-white md:w-full md:flex-row",
          containerClassName,
        ),
      [containerClassName],
    );

    return (
      <div className={rootClasses}>
        {tripType === TRIP_TYPES.ONEWAY ? (
          <div className="flex w-full items-center gap-3">
            <div className="flex-1">
              <GenericDatePicker
                ref={departureDatePickerRef}
                id="departure-date"
                label={tForm("dateSelectionDepartureDate")}
                onSelect={onSelectDepartureDate}
                value={departureDate}
                onPortalStateChange={setIsDepartureDatePickerOpen}
                onOpen={onOpen}
                onDropdownOpen={onDropdownOpen}
                isFromDate={true}
                defaultMonth={departureDate}
                mobileDepartureClick={handleMobileDepartureDateClick}
                mobileReturnClick={handleMobileReturnDateClick}
                departureDate={departureDate}
                returnDate={null}
                isRoundTrip={false}
                publicHolidays={publicHolidays}
                translationNamespace={translationNamespace}
                dayButtonComponent={dayButtonComponent}
                getDayMeta={getDayMeta}
                inputClassName={inputClassName}
                viewportHeightWrapperActive={viewportHeightWrapperActive}
              />
            </div>

            {showQuickButtons && (
              <div className={clsx("flex gap-2.5", quickButtonsWrapperClassName)}>
                <button
                  className={clsx(
                    "cursor-pointer flex items-center justify-center gap-2 rounded-[9px] border px-3 py-2 text-center text-sm leading-normal transition-colors",
                    isTodaySelected
                      ? "border-[#01007F] text-[#01007F] font-medium"
                      : "border-[#E1E1EF] text-[#585B5E] font-normal",
                  )}
                  onClick={() => {
                    setDepartureDate(today);
                    setReturnDate(today);
                    emitChange(today, today);
                  }}
                >
                  {tForm("dateSelectionToday")}
                </button>
                <button
                  className={clsx(
                    "cursor-pointer flex items-center justify-center gap-2 rounded-[9px] border px-3 py-2 text-center text-sm leading-normal transition-colors",
                    isTomorrowSelected
                      ? "border-[#01007F] text-[#01007F] font-medium"
                      : "border-[#E1E1EF] text-[#585B5E] font-normal",
                  )}
                  onClick={() => {
                    setDepartureDate(tomorrow);
                    setReturnDate(tomorrow);
                    emitChange(tomorrow, tomorrow);
                  }}
                >
                  {tForm("dateSelectionTomorrow")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {activeTab === 0 ? (
              <div className="border-primary-weak flex w-full items-center border-b md:w-full md:border-0">
                <div className="w-1/2">
                  <GenericDatePicker
                    ref={departureDatePickerRef}
                    id="departure-date"
                    label={tForm("dateSelectionDepartureDate")}
                    onSelect={onSelectDepartureDate}
                    value={departureDate}
                    onPortalStateChange={setIsDepartureDatePickerOpen}
                    onOpen={onOpen}
                    onDropdownOpen={onDropdownOpen}
                    isFromDate={true}
                    defaultMonth={departureDate}
                    mobileDepartureClick={handleMobileDepartureDateClick}
                    mobileReturnClick={handleMobileReturnDateClick}
                    departureDate={departureDate}
                    returnDate={returnDate}
                    isRoundTrip={activeTab === 1}
                    publicHolidays={publicHolidays}
                    translationNamespace={translationNamespace}
                    dayButtonComponent={dayButtonComponent}
                    getDayMeta={getDayMeta}
                    viewportHeightWrapperActive={viewportHeightWrapperActive}
                  />
                </div>
                <span className="bg-border h-[1px] w-4" />
                <div className="w-1/2">
                  <button
                    className={clsx(
                      "text-foreground-muted h-full w-full cursor-pointer items-center justify-center gap-2 px-[18px] py-5",
                      activeTab == 0 ? "flex" : "hidden",
                    )}
                    onClick={handleAddReturnClick}
                  >
                    <span>{tForm("dateSelectionAddReturn")}</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="w-full md:w-full">
                <GenericDateRangePicker
                  ref={returnDatePickerRef}
                  id="return-date"
                  label={tForm("dateSelectionReturnDate")}
                  onSelect={onSelectReturnDate}
                  value={{ from: departureDate, to: returnDate }}
                  disabled={departureDate}
                  onPortalStateChange={setIsReturnDatePickerOpen}
                  onOpen={onOpen}
                  onDropdownOpen={onDropdownOpen}
                  isFromDate={false}
                  defaultMonth={departureDate}
                  mobileDepartureClick={handleMobileDepartureDateClick}
                  mobileReturnClick={handleMobileReturnDateClick}
                  departureDate={departureDate}
                  returnDate={returnDate}
                  isRoundTrip={activeTab === 1}
                  publicHolidays={publicHolidays}
                  translationNamespace={translationNamespace}
                  dayButtonComponent={dayButtonComponent}
                  getDayMeta={getDayMeta}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  },
);

TripDatePicker.displayName = "TripDatePicker";

export default TripDatePicker;


